import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  useGetCompanyProductsQuery,
  useCreateCompanyProductMutation,
  useUpdateCompanyProductMutation,
  useDeleteCompanyProductMutation,
  useUpdateProductPricingMutation,
} from '../../../gql/generated/graphql';
import { useCreateChatSession, useChatMessages, useSendMessage, useChatSessions } from '../../../hooks/useChatApi';
import { useTenant } from '../../../contexts/tenant-context';
import type { ChatMessage } from '../../../types/chat';

interface Props {
  companyId: number;
  companyName?: string;
}

const PRODUCT_TYPES = ['core', 'add-on', 'module', 'service', 'platform'];

interface PricingTier {
  pricingModelType: string;
  priceDisplay: string;
  priceNumeric: number | null;
  currency: string;
  billingPeriod: string;
  tierName: string;
}

const emptyTier = (): PricingTier => ({
  pricingModelType: '',
  priceDisplay: '',
  priceNumeric: null,
  currency: 'EUR',
  billingPeriod: '',
  tierName: '',
});

const ProductsBusinessModelStep = ({ companyId, companyName }: Props) => {
  const { selectedTenantId: tenantId } = useTenant();
  const userId = 1; // TODO: derive from auth context

  // ---- Products DataGrid ----
  const { data, loading, refetch: refetchProducts } = useGetCompanyProductsQuery({ variables: { companyId } });
  const products = data?.getCompanyProducts?.edges?.map(edge => edge?.node).filter(Boolean) || [];

  // ---- Mutations ----
  const [createProduct] = useCreateCompanyProductMutation();
  const [updateProduct] = useUpdateCompanyProductMutation();
  const [deleteProduct] = useDeleteCompanyProductMutation();
  const [updatePricing] = useUpdateProductPricingMutation();

  // ---- Toast ----
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // ---- Add Product dialog ----
  const [addOpen, setAddOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({ productName: '', description: '', productType: '' });

  // ---- Pricing dialog ----
  const [pricingOpen, setPricingOpen] = useState(false);
  const [pricingProductId, setPricingProductId] = useState<number | null>(null);
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([emptyTier()]);

  // ---- Chat session management ----
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Find existing product_manager session for this company
  const { data: sessionsData } = useChatSessions(userId, tenantId, 50, 0, 'product_manager', companyId);

  useEffect(() => {
    if (sessionsData?.sessions?.length && !sessionId) {
      setSessionId(sessionsData.sessions[0].id);
    }
  }, [sessionsData, sessionId]);

  // Create session mutation
  const createSession = useCreateChatSession();

  const ensureSession = useCallback(async (): Promise<string> => {
    if (sessionId) return sessionId;

    const session = await createSession.mutateAsync({
      userId,
      tenantId: tenantId ?? 1,
      agentType: 'product_manager',
      companyId,
      companyName: companyName || `Company #${companyId}`,
    });
    setSessionId(session.id);
    return session.id;
  }, [sessionId, createSession, userId, tenantId, companyId, companyName]);

  // Messages
  const { data: messagesData } = useChatMessages(userId, tenantId, sessionId);
  const messages: ChatMessage[] = messagesData?.messages || [];

  // Send message
  const sendMessage = useSendMessage();

  const handleSend = useCallback(async () => {
    const content = inputValue.trim();
    if (!content || sendMessage.isPending) return;

    setInputValue('');

    try {
      const sid = await ensureSession();
      await sendMessage.mutateAsync({
        userId,
        tenantId: tenantId ?? 1,
        sessionId: sid,
        content,
      });
      refetchProducts();
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  }, [inputValue, sendMessage, ensureSession, userId, tenantId, refetchProducts]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sendMessage.isPending]);

  // ---- Inline edit handler ----
  const handleProcessRowUpdate = async (newRow: any, oldRow: any) => {
    const changed: Record<string, any> = {};
    for (const key of ['productName', 'description', 'productType']) {
      if (newRow[key] !== oldRow[key]) changed[key] = newRow[key];
    }
    if (Object.keys(changed).length === 0) return newRow;

    const result = await updateProduct({
      variables: { productId: newRow.dbId, ...changed },
    });
    if (result.data?.updateCompanyProduct?.success) {
      setToast({ message: 'Updated', severity: 'success' });
      return newRow;
    }
    setToast({
      message: result.data?.updateCompanyProduct?.message || 'Update failed',
      severity: 'error',
    });
    return oldRow;
  };

  // ---- Delete handler ----
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this product?')) return;
    const result = await deleteProduct({ variables: { productId: id } });
    if (result.data?.deleteCompanyProduct?.success) {
      setToast({ message: 'Deleted', severity: 'success' });
      refetchProducts();
    } else {
      setToast({ message: 'Delete failed', severity: 'error' });
    }
  };

  // ---- Add product handler ----
  const handleAddProduct = async () => {
    const result = await createProduct({
      variables: {
        companyId,
        productName: newProduct.productName,
        description: newProduct.description || undefined,
        productType: newProduct.productType || undefined,
      },
    });
    if (result.data?.createCompanyProduct?.success) {
      setToast({ message: 'Product created', severity: 'success' });
      setAddOpen(false);
      setNewProduct({ productName: '', description: '', productType: '' });
      refetchProducts();
    } else {
      setToast({ message: result.data?.createCompanyProduct?.message || 'Create failed', severity: 'error' });
    }
  };

  // ---- Pricing dialog handlers ----
  const openPricingDialog = (row: any) => {
    setPricingProductId(row.dbId);
    const existing = (row.pricingModels || []).map((m: any) => ({
      pricingModelType: m.pricingModelType || '',
      priceDisplay: m.priceDisplay || '',
      priceNumeric: m.priceNumeric ?? null,
      currency: m.currency || 'EUR',
      billingPeriod: m.billingPeriod || '',
      tierName: m.tierName || '',
    }));
    setPricingTiers(existing.length > 0 ? existing : [emptyTier()]);
    setPricingOpen(true);
  };

  const handleSavePricing = async () => {
    if (!pricingProductId) return;
    const result = await updatePricing({
      variables: {
        productId: pricingProductId,
        pricing: pricingTiers.map(t => ({
          pricingModelType: t.pricingModelType || undefined,
          priceDisplay: t.priceDisplay || undefined,
          priceNumeric: t.priceNumeric ?? undefined,
          currency: t.currency || undefined,
          billingPeriod: t.billingPeriod || undefined,
          tierName: t.tierName || undefined,
        })),
      },
    });
    if (result.data?.updateProductPricing?.success) {
      setToast({ message: 'Pricing updated', severity: 'success' });
      setPricingOpen(false);
      refetchProducts();
    } else {
      setToast({ message: 'Pricing update failed', severity: 'error' });
    }
  };

  const updateTier = (index: number, field: keyof PricingTier, value: any) => {
    setPricingTiers(prev => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  };

  // ---- DataGrid columns ----
  const columns: GridColDef[] = [
    { field: 'productName', headerName: 'Product', flex: 1, minWidth: 140, editable: true },
    { field: 'description', headerName: 'Description', flex: 2, minWidth: 160, editable: true },
    {
      field: 'productType',
      headerName: 'Type',
      width: 120,
      editable: true,
      type: 'singleSelect',
      valueOptions: PRODUCT_TYPES,
    },
    {
      field: 'softwareCategory',
      headerName: 'Category',
      width: 160,
      valueGetter: (value, row) => row?.softwareCategory?.subCategory1 || '-',
    },
    {
      field: 'pricing',
      headerName: 'Pricing',
      width: 160,
      renderCell: params => {
        const models = params.row?.pricingModels || [];
        return (
          <Box display="flex" gap={0.5} alignItems="center">
            {models.length === 0 ? (
              <Typography variant="caption">-</Typography>
            ) : (
              models
                .slice(0, 2)
                .map((m: any) => (
                  <Chip key={m.dbId} label={m.priceDisplay || m.pricingModelType} size="small" variant="outlined" />
                ))
            )}
            <Tooltip title="Edit pricing">
              <IconButton size="small" onClick={() => openPricingDialog(params.row)}>
                <EditIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 90,
      renderCell: params => <Chip label={params.value || 'unknown'} size="small" variant="outlined" />,
    },
    {
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: params => (
        <IconButton size="small" onClick={() => handleDelete(params.row.dbId)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Grid container spacing={2}>
        {/* Left: Products DataGrid */}
        <Grid item xs={12} md={7}>
          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Products
              </Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
                Add Product
              </Button>
            </Box>
            <DataGrid
              rows={products}
              getRowId={row => row.dbId}
              columns={columns}
              loading={loading}
              processRowUpdate={handleProcessRowUpdate}
              autoHeight
              disableRowSelectionOnClick
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              pageSizeOptions={[10]}
              sx={{
                fontFamily: 'var(--font-data)',
                fontSize: 'var(--text-sm)',
                '& .MuiDataGrid-cell': { borderBottom: '1px solid var(--border-subtle)' },
              }}
            />
          </Paper>
        </Grid>

        {/* Right: Chat Panel */}
        <Grid item xs={12} md={5}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 0,
              height: 520,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Chat header */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                <SmartToyIcon fontSize="small" color="primary" />
                Product Manager
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Describe products to add, update, or remove them
              </Typography>
            </Box>

            {/* Messages area */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                px: 2,
                py: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              {messages.length === 0 && !sendMessage.isPending && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    opacity: 0.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Describe the company's products, pricing, and target market.
                    <br />
                    The agent will extract structured data and match software categories.
                  </Typography>
                </Box>
              )}

              {messages.map(msg => (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                  }}
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: msg.role === 'user' ? 'primary.main' : 'action.selected',
                      flexShrink: 0,
                      mt: 0.5,
                    }}
                  >
                    {msg.role === 'user' ? (
                      <PersonIcon sx={{ fontSize: 16, color: 'primary.contrastText' }} />
                    ) : (
                      <SmartToyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    )}
                  </Box>
                  <Box
                    sx={{
                      maxWidth: '85%',
                      backgroundColor: msg.role === 'user' ? 'primary.main' : 'action.hover',
                      color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                      fontSize: '0.8125rem',
                      lineHeight: 1.5,
                      '& p': { m: 0 },
                      '& p + p': { mt: 0.5 },
                      '& ul, & ol': { m: 0, pl: 2 },
                      '& table': {
                        borderCollapse: 'collapse',
                        fontSize: '0.75rem',
                        mt: 0.5,
                        '& td, & th': {
                          border: '1px solid',
                          borderColor: 'divider',
                          px: 0.5,
                          py: 0.25,
                        },
                      },
                      '& code': {
                        backgroundColor: 'action.selected',
                        borderRadius: 0.5,
                        px: 0.5,
                        fontSize: '0.75rem',
                      },
                    }}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                  </Box>
                </Box>
              ))}

              {sendMessage.isPending && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'action.selected',
                      flexShrink: 0,
                    }}
                  >
                    <SmartToyIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </Box>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Thinking...
                  </Typography>
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>

            {/* Input area */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                gap: 1,
                alignItems: 'flex-end',
              }}
            >
              <TextField
                fullWidth
                multiline
                maxRows={3}
                size="small"
                placeholder="e.g. They sell a CRM for €50/mo and an ERP suite..."
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={sendMessage.isPending}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.8125rem',
                    borderRadius: 2,
                  },
                }}
              />
              <IconButton
                color="primary"
                onClick={handleSend}
                disabled={!inputValue.trim() || sendMessage.isPending}
                size="small"
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Add Product Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Product</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            label="Product Name"
            required
            value={newProduct.productName}
            onChange={e => setNewProduct({ ...newProduct, productName: e.target.value })}
          />
          <TextField
            label="Description"
            multiline
            rows={2}
            value={newProduct.description}
            onChange={e => setNewProduct({ ...newProduct, description: e.target.value })}
          />
          <FormControl>
            <InputLabel>Type</InputLabel>
            <Select
              value={newProduct.productType}
              label="Type"
              onChange={e => setNewProduct({ ...newProduct, productType: e.target.value })}
            >
              <MenuItem value="">None</MenuItem>
              {PRODUCT_TYPES.map(t => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddProduct} disabled={!newProduct.productName}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pricing Edit Dialog */}
      <Dialog open={pricingOpen} onClose={() => setPricingOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Pricing Tiers</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          {pricingTiers.map((tier, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2">Tier {idx + 1}</Typography>
                {pricingTiers.length > 1 && (
                  <IconButton size="small" onClick={() => setPricingTiers(prev => prev.filter((_, i) => i !== idx))}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Grid container spacing={1.5}>
                <Grid item xs={4}>
                  <TextField
                    label="Tier Name"
                    size="small"
                    fullWidth
                    value={tier.tierName}
                    onChange={e => updateTier(idx, 'tierName', e.target.value)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Pricing Model"
                    size="small"
                    fullWidth
                    value={tier.pricingModelType}
                    onChange={e => updateTier(idx, 'pricingModelType', e.target.value)}
                    placeholder="e.g. subscription, one-time"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Price Display"
                    size="small"
                    fullWidth
                    value={tier.priceDisplay}
                    onChange={e => updateTier(idx, 'priceDisplay', e.target.value)}
                    placeholder="e.g. €50/mo"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Price (numeric)"
                    size="small"
                    fullWidth
                    type="number"
                    value={tier.priceNumeric ?? ''}
                    onChange={e => updateTier(idx, 'priceNumeric', e.target.value ? parseFloat(e.target.value) : null)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Currency"
                    size="small"
                    fullWidth
                    value={tier.currency}
                    onChange={e => updateTier(idx, 'currency', e.target.value)}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    label="Billing Period"
                    size="small"
                    fullWidth
                    value={tier.billingPeriod}
                    onChange={e => updateTier(idx, 'billingPeriod', e.target.value)}
                    placeholder="e.g. monthly, annual"
                  />
                </Grid>
              </Grid>
            </Paper>
          ))}
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setPricingTiers(prev => [...prev, emptyTier()])}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add Tier
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPricingOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePricing}>
            Save Pricing
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default ProductsBusinessModelStep;
